import { useReactFlow } from '@xyflow/react';
import { useCallback, useMemo } from 'react';

import {
  GraphVizTileEmphasis,
  parseTileEmphasis,
} from '@/modules/graphViz/graphVizNodeConfig';

import { Node } from '../types';
import { isTileNode } from '../utils/nodes';

/**
 * Shared hook for reading and changing the tile emphasis (contrast) of
 * selected tiles (standard and icon). Used by the EntityMap (ownership) and
 * EntityDiagram (single entity) edit trowsers. Uses `updateNode` for
 * immediate-mode updates that flow through each diagram's `onNodesChange` →
 * reducer path.
 *
 * The estate waterfall has its own version at
 * `src/modules/estateWaterfall/hooks/useSelectedTileEmphasis.ts` that
 * dispatches UPDATE_TILE_CONFIG through its reducer for undo/redo support.
 *
 * Unlike iconShape changes, emphasis is a pure color swap so we do not need
 * the post-update height-reset dance.
 */
export function useSelectedTileEmphasis(nodes: Node[]) {
  const { updateNode, getNode } = useReactFlow();

  const selectedTiles = useMemo(
    () => nodes.filter((n): n is Node => !!n.selected && isTileNode(n)),
    [nodes]
  );

  const hasEmphasizableTilesSelected = selectedTiles.length > 0;

  const { currentEmphasis, isMixed } = useMemo((): {
    currentEmphasis: GraphVizTileEmphasis;
    isMixed: boolean;
  } => {
    if (selectedTiles.length === 0)
      return { currentEmphasis: GraphVizTileEmphasis.Low, isMixed: false };

    const firstEmphasis =
      parseTileEmphasis(selectedTiles[0]?.data.tileEmphasis as string) ??
      GraphVizTileEmphasis.Low;
    const allSame = selectedTiles.every(
      (n) =>
        (parseTileEmphasis(n.data.tileEmphasis as string) ??
          GraphVizTileEmphasis.Low) === firstEmphasis
    );

    return allSame
      ? { currentEmphasis: firstEmphasis, isMixed: false }
      : { currentEmphasis: GraphVizTileEmphasis.Low, isMixed: true };
  }, [selectedTiles]);

  const setTileEmphasis = useCallback(
    (emphasis: GraphVizTileEmphasis) => {
      for (const tile of selectedTiles) {
        const n = getNode(tile.id);
        if (!n) continue;
        updateNode(tile.id, {
          data: { ...n.data, tileEmphasis: emphasis },
        });
      }
    },
    [selectedTiles, updateNode, getNode]
  );

  return {
    hasEmphasizableTilesSelected,
    currentEmphasis,
    isMixed,
    setTileEmphasis,
  };
}
